import { IProjectConfig, projects } from '@hawkindynamics/core-client';
import { FirebaseApp, getApp, initializeApp } from 'firebase/app';
import {
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { Auth, getAuth, RecaptchaVerifier } from 'firebase/auth';
import {
  collection,
  CollectionReference,
  getDocs,
  getFirestore,
} from 'firebase/firestore';
import { getJsonField } from '../../utils/json';
import {
  IEmailIdentificationPayload,
  IEmailIdentificationResponse,
  IPermission,
} from '../types';
import {
  clearTenantConfig,
  getStoredTenantConfig,
  globalApp,
  globalAppCheck,
  setTenantApp,
  setTenantAppCheck,
  tenantApp$,
} from './appsProvider';

/**
 * @description sends email identification payload to identity service and returns provider data
 * @param payload - Email identification payload
 * @returns Promise that resolves with projectId, sign_in_providers, tenant info, and email
 */
export const sendEmailIdentification = async (
  payload: IEmailIdentificationPayload
): Promise<IEmailIdentificationResponse> => {
  // Determine environment from global app's project ID
  const isDev =
    globalApp.options.projectId?.includes('-dev') ||
    window.location.hostname === 'cloud.dev.hawkindynamics.com';
  const IDENTITY_SERVICE_URL = isDev
    ? 'https://identity.global-dev.hawkindynamics.com/locate'
    : 'https://identity.global.hawkindynamics.com/locate';

  // Get App Check token from the global app
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (globalAppCheck) {
    try {
      const token = await getToken(globalAppCheck);
      headers['X-Firebase-AppCheck'] = token.token;
    } catch (err) {
      console.error('Failed to get App Identification token');
    }
  }

  let response: Response;
  try {
    response = await fetch(IDENTITY_SERVICE_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch (err) {
    // Network error (no connection, timeout, etc.)
    throw new Error('NETWORK_ERROR');
  }

  // Handle specific HTTP status codes
  if (!response.ok) {
    switch (response.status) {
      case 400:
        throw new Error('INVALID_REQUEST');
      case 403:
        throw new Error('APP_CHECK_FAILED');
      case 404:
        throw new Error('USER_NOT_FOUND');
      case 429:
        throw new Error('RATE_LIMIT_EXCEEDED');
      case 503:
        throw new Error('SERVICE_UNAVAILABLE');
      default:
        if (response.status >= 500) {
          throw new Error('SERVER_ERROR');
        }
        throw new Error(
          `Identity lookup failed: ${response.status} ${response.statusText}`
        );
    }
  }

  let data: any;
  try {
    data = await response.json();
  } catch (err) {
    throw new Error('RESPONSE_PARSE_ERROR');
  }

  // Handle multi-tenant response format
  if (!data.tenants || data.tenants.length === 0) {
    throw new Error('USER_NOT_FOUND');
  }

  // For single tenant, return in existing format
  if (data.tenants.length === 1) {
    const tenant = data.tenants[0];
    // Extract app_config - it might be nested in a wrapper object or direct
    const rawAppConfig = tenant.app_config as any;
    const appConfig = rawAppConfig?.config || rawAppConfig;
    const projectId = getJsonField<string>(appConfig, 'projectId');

    return {
      projectId,
      email: payload.email,
      sign_in_providers: tenant.sign_in_providers,
      tenant_id: tenant.tenant_id,
      org_id: tenant.org_id,
      app_config: appConfig,
    };
  }

  // For multiple tenants, return all for user selection
  const firstTenant = data.tenants[0];
  const rawAppConfig = firstTenant.app_config as any;
  const appConfig = rawAppConfig?.config || rawAppConfig;
  const projectId = getJsonField<string>(appConfig, 'projectId');

  return {
    projectId,
    email: payload.email,
    sign_in_providers: firstTenant.sign_in_providers,
    tenants: data.tenants,
  };
};

/**
 * Creates a RecaptchaVerifier for the tenant app
 * @param elementId - The ID of the HTML element to render the reCAPTCHA
 * @returns RecaptchaVerifier instance
 */
export const createRecaptchaVerifier = (
  elementId: string
): RecaptchaVerifier => {
  const app = tenantApp$.value;
  if (!app) {
    throw new Error('Tenant app not initialized');
  }
  const auth = getAuth(app);
  return new RecaptchaVerifier(auth, elementId, {
    size: 'invisible',
    callback: (): void => null,
    'expired-callback': (): void => null,
  });
};

/**
 * Initialize or get Firebase app for a tenant with proper tenant context
 * @param appConfig - Firebase configuration from identity service
 * @param tenantId - Firebase tenant ID (null for default tenant)
 * @returns Auth instance configured for the tenant
 */
export const getAuthForTenant = (
  appConfig: IEmailIdentificationResponse['app_config'],
  tenantId: string | null
): Auth => {
  if (!appConfig) {
    throw new Error(
      'Firebase app configuration is required for tenant authentication'
    );
  }

  // Use getJsonField to support both snake_case and camelCase from API response
  const projectId = getJsonField<string>(appConfig, 'projectId')!;
  const appName = tenantId ? `app-${tenantId}` : `app-${projectId}`;

  // Check if app already exists
  let app: FirebaseApp;
  let isNewApp = false;
  try {
    app = getApp(appName);
  } catch {
    // App doesn't exist, initialize it with the config from identity service
    isNewApp = true;
    const firebaseConfig = {
      apiKey: getJsonField<string>(appConfig, 'apiKey')!,
      authDomain:
        getJsonField<string>(appConfig, 'authDomain') ??
        `${projectId}.firebaseapp.com`,
      databaseURL: getJsonField<string>(appConfig, 'databaseURL'),
      projectId: projectId,
      storageBucket: getJsonField<string>(appConfig, 'storageBucket'),
      messagingSenderId: getJsonField<string>(appConfig, 'messagingSenderId')!,
      appId: getJsonField<string>(appConfig, 'appId')!,
    };
    app = initializeApp(firebaseConfig, appName);

    // Initialize App Check before authentication (only for new apps)
    const project = projects.find(
      (proj: IProjectConfig) => proj.id === projectId
    );
    if (project) {
      try {
        const appCheck = initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(
            project.recaptchaEnterpriseKey
          ),
          isTokenAutoRefreshEnabled: true,
        });
        // Store the AppCheck instance for use in other parts of the app (e.g., SAML bridge)
        setTenantAppCheck(appName, appCheck);
      } catch (e) {
        console.error(
          'Failed to initialize App Identification for tenant app:',
          appName,
          e
        );
      }
    }
  }

  // Set as current tenant app - triggers reactive auth state subscriptions
  // (do this regardless of whether app is new or existing)
  if (isNewApp || tenantApp$.value?.name !== appName) {
    setTenantApp(app);
  }

  const auth = getAuth(app);

  // Set tenant ID if provided (for SSO tenants)
  if (tenantId) {
    auth.tenantId = tenantId;
  }

  return auth;
};

/**
 * @description fetches user organizations/permissions from Firestore
 * @param userId - The user's Firebase UID
 * @returns Array of user permissions (organizations)
 */
export const getUserOrganizations = async (
  userId: string
): Promise<IPermission[]> => {
  const app = tenantApp$.value;

  if (!app) {
    throw new Error('Tenant app not initialized');
  }

  const firestore = getFirestore(app);
  const teamsCollection = collection(
    firestore,
    `userProfiles/${userId}/teams`
  ) as CollectionReference;

  const snapshot = await getDocs(teamsCollection);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as {
      read: boolean;
      write: boolean;
      orgId: string;
      teamName?: string;
      role?: string;
    }),
  }));
};

/**
 * Restore tenant app from localStorage on module load
 * This runs BEFORE userProvider.ts subscribes to auth state
 * This ensures that if a user has a valid Firebase session, the tenant app
 * is already initialized and the auth state subscription will detect them
 */
const restoreTenantAppOnStartup = (): void => {
  const storedConfig = getStoredTenantConfig();
  if (storedConfig) {
    try {
      // Initialize the tenant app - this will call setTenantApp() which triggers
      // the auth state subscription in userProvider.ts
      getAuthForTenant(storedConfig.appConfig, storedConfig.tenantId);
    } catch (e) {
      // Clear invalid config
      clearTenantConfig();
    }
  }
};

// Execute restoration on module load
// Module load order: appsProvider.ts -> auth.ts -> userProvider.ts
// So this runs before userProvider subscribes to tenantApp$
restoreTenantAppOnStartup();
